import { trackAnalyticsEvent } from '@autone/analytics';
import { useAutoneTranslation } from '@autone/translations';
import { useIsMobile } from '@autone/utils';
import { Menu } from 'lucide-react';
import * as React from 'react';

import {
  Button,
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  TooltipProvider,
} from '../../../atoms';
import { Separator } from '../../../atoms/Separator';
import { ExpandIcon } from '../../../icons';
import { cn } from '../../../utils';
import { SidebarContext, useSidebarContext } from '../context/SidebarContext';

const SIDEBAR_WIDTH = '267px';
const SIDEBAR_WIDTH_ICON = '72px';
const COLLAPSE_EXPAND_DURATION = '200ms';
const SIDEBAR_KEYBOARD_SHORTCUT = 'b';
const SIDEBAR_STORAGE_KEY = 'sidebar_state';

export interface SidebarContextProps {
  state: 'expanded' | 'collapsed';
  open: boolean;
  setOpen: (open: boolean) => void;
  openMobile: boolean;
  setOpenMobile: (open: boolean) => void;
  isMobile: boolean;
  toggleSidebar: () => void;
}

function SidebarProvider({
  className,
  style,
  children,
  ...props
}: React.ComponentProps<'div'>) {
  const isMobile = useIsMobile();
  const [openMobile, setOpenMobile] = React.useState(false);

  // Initialize from localStorage, fallback to false (closed)
  const [open, setOpen] = React.useState<boolean>(() => {
    const stored = localStorage.getItem(SIDEBAR_STORAGE_KEY);
    if (stored !== null) {
      return stored === 'true';
    }
    return false;
  });

  // Persist changes
  const updateOpen = React.useCallback((next: boolean) => {
    setOpen(next);
    localStorage.setItem(SIDEBAR_STORAGE_KEY, String(next));
  }, []);

  // Toggle helper
  const toggleSidebar = React.useCallback(() => {
    return isMobile ? setOpenMobile((prev) => !prev) : updateOpen(!open);
  }, [isMobile, open, updateOpen]);

  // Keyboard shortcut
  React.useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (
        event.key === SIDEBAR_KEYBOARD_SHORTCUT &&
        (event.metaKey || event.ctrlKey)
      ) {
        event.preventDefault();
        toggleSidebar();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [toggleSidebar]);

  const state = open ? 'expanded' : 'collapsed';

  const contextValue = React.useMemo<SidebarContextProps>(
    () => ({
      state,
      open,
      setOpen: updateOpen,
      isMobile,
      openMobile,
      setOpenMobile,
      toggleSidebar,
    }),
    [
      state,
      open,
      updateOpen,
      isMobile,
      openMobile,
      setOpenMobile,
      toggleSidebar,
    ],
  );

  return (
    <SidebarContext.Provider value={contextValue}>
      <TooltipProvider delayDuration={0}>
        <div
          data-slot="sidebar-wrapper"
          style={
            {
              '--sidebar-width': SIDEBAR_WIDTH,
              '--sidebar-width-icon': SIDEBAR_WIDTH_ICON,
              '--sidebar-transition-duration': COLLAPSE_EXPAND_DURATION,
              ...style,
            } as React.CSSProperties
          }
          className={cn(
            'dark group/sidebar-wrapper has-data-[variant=inset]:bg-sidebar flex min-h-svh z-[1100]',
            className,
          )}
          {...props}
        >
          {children}
        </div>
      </TooltipProvider>
    </SidebarContext.Provider>
  );
}

function Sidebar({
  className,
  children,
  ...props
}: React.ComponentProps<'div'>) {
  const { common } = useAutoneTranslation();
  const { isMobile, state, openMobile, setOpenMobile } = useSidebarContext();

  if (isMobile) {
    return (
      <>
        {!openMobile && (
          <Button
            id="open-sidebar-button"
            aria-label={common('sidenav.open-sidebar')}
            size="icon"
            variant="secondary"
            className="absolute top-2 left-2 z-[110]"
            onClick={() => {
              trackAnalyticsEvent({
                category: 'sidebar',
                action: 'mobile:open_sidebar_clicked',
              });
              setOpenMobile(true);
            }}
          >
            <Menu />
          </Button>
        )}
        <Sheet open={openMobile} onOpenChange={setOpenMobile} {...props}>
          <SheetContent
            id="sidebar-sheet-content"
            data-sidebar="sidebar"
            data-slot="sidebar"
            data-mobile="true"
            className="dark bg-sidebar text-sidebar-foreground w-[var(--sidebar-width)] [&>button]:hidden z-[110]"
            side="left"
          >
            <SheetHeader className="sr-only">
              <SheetTitle>Sidebar</SheetTitle>
              <SheetDescription>Displays the mobile sidebar.</SheetDescription>
            </SheetHeader>
            <div
              className="flex h-full w-full flex-col"
              data-state="expanded"
              data-collapsible=""
            >
              {children}
            </div>
          </SheetContent>
        </Sheet>
      </>
    );
  }

  return (
    <div
      className="group peer text-sidebar-foreground hidden md:block"
      data-state={state}
      data-collapsible={state === 'collapsed' ? 'icon' : ''}
      data-variant="sidebar"
      data-side="left"
      data-slot="sidebar"
    >
      {/* This is what handles the sidebar gap on desktop */}
      <div
        data-slot="sidebar-gap"
        className={cn(
          'relative w-[var(--sidebar-width)] bg-transparent transition-[width] ease-linear',
          'group-data-[collapsible=icon]:w-[var(--sidebar-width-icon)]',
        )}
        style={{ transitionDuration: 'var(--sidebar-transition-duration)' }}
      />
      <div
        data-slot="sidebar-container"
        className={cn(
          'fixed inset-y-0 z-10 hidden h-svh w-[var(--sidebar-width)] ease-linear md:flex',
          'left-0',
          'group-data-[collapsible=icon]:w-[var(--sidebar-width-icon)]',
          className,
        )}
        style={{ transitionDuration: 'var(--sidebar-transition-duration)' }}
        {...props}
      >
        <div
          data-sidebar="sidebar"
          data-slot="sidebar-inner"
          className="bg-sidebar flex h-full w-full flex-col relative"
        >
          {children}
        </div>
      </div>
    </div>
  );
}

function SidebarTrigger({
  className,
  onClick,
  ...props
}: Omit<React.ComponentProps<typeof Button>, 'children'>) {
  const { toggleSidebar, open } = useSidebarContext();

  const handleToggleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    trackAnalyticsEvent({
      category: 'sidebar',
      action: 'expand_collapse_button_clicked',
      properties: {
        open,
      },
    });

    onClick?.(event);
    toggleSidebar();
  };

  return (
    <Button
      data-sidebar="trigger"
      data-slot="sidebar-trigger"
      size="icon"
      variant="ghost"
      className={cn(
        'flex items-center justify-center h-5 w-5 p-0 bg-transparent top-[40px] left-[calc(100%-10px)] absolute text-muted-foreground z-50 rounded-full',
        className,
      )}
      style={{ transitionDuration: 'var(--sidebar-transition-duration)' }}
      onClick={handleToggleClick}
      {...props}
    >
      <ExpandIcon className={cn('h-5 w-5', open && 'rotate-180')} />
      <span className="sr-only">Toggle Sidebar</span>
    </Button>
  );
}

function SidebarRail({ className, ...props }: React.ComponentProps<'button'>) {
  const { common } = useAutoneTranslation();
  const { toggleSidebar, open } = useSidebarContext();
  const shortcut = navigator.platform.toLowerCase().includes('mac')
    ? '⌘B'
    : 'Ctrl+B';

  const handleRailClick = () => {
    trackAnalyticsEvent({
      category: 'sidebar',
      action: 'expand_collapse_rail_clicked',
      properties: { expand: !open },
    });
    toggleSidebar();
  };
  const label = common('sidenav.toggle-sidebar') + `(${shortcut})`;

  return (
    <button
      type="button"
      data-sidebar="rail"
      data-slot="sidebar-rail"
      aria-label={label}
      tabIndex={-1}
      onClick={handleRailClick}
      title={label}
      className={cn(
        'cursor-w-resize',
        'absolute inset-y-0 z-20 w-4 border-0 -translate-x-1/2 -right-4 sm:flex',
        'after:absolute after:inset-y-0 after:left-1/2 after:w-[1px]',
        'cursor-w-resize',
        '[[data-state=collapsed]_&]:cursor-e-resize',
        className,
      )}
      {...props}
    />
  );
}

function SidebarSeparator({
  className,
  ...props
}: React.ComponentProps<typeof Separator>) {
  return (
    <Separator
      data-slot="sidebar-separator"
      data-sidebar="separator"
      className={cn('bg-sidebar-border px-2 my-1.5 w-auto', className)}
      {...props}
    />
  );
}

function SidebarFooter({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="sidebar-footer"
      data-sidebar="footer"
      className={cn('flex flex-col gap-1.5 pt-2 pb-8 px-4', className)}
      {...props}
    />
  );
}

function SidebarContent({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="sidebar-content"
      data-sidebar="content"
      className={cn(
        'flex min-h-0 flex-1 flex-col gap-2 overflow-auto overflow-x-hidden no-scrollbar',
        className,
      )}
      {...props}
    />
  );
}

export {
  Sidebar,
  SidebarContent,
  SidebarSeparator,
  SidebarFooter,
  SidebarProvider,
  SidebarRail,
  SidebarTrigger,
};
